/**
 * ConceptVideoModal.tsx
 * ─────────────────────────────────────────────────────────────
 * 파편 학습 핵심 컴포넌트
 *
 * 사용 흐름:
 *   문제 틀림 → "🎬 영상으로 복습" 클릭
 *   → 해당 개념의 승인된 영상 목록 표시
 *   → 영상 선택 → YouTube embed 재생
 *   → 30초+ 시청 → 유효 시청 판정 → 크리에이터 크레딧 +10원
 *   → 별점 평가 → "복습 완료!" → 원래 문제로 복귀
 */

import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  X, Play, Star, Clock, Eye, CheckCircle, ChevronRight,
  Youtube, Upload, AlertCircle, ThumbsUp, Award, Loader2,
  BookOpen, ArrowLeft,
} from 'lucide-react'
import {
  useVideoStore,
  ConceptVideo,
  CREDIT_PER_VIEW,
  MIN_WATCH_SEC,
  getYouTubeEmbedUrl,
  CONCEPT_TO_STEP,
} from '../store/videoStore'

// ─────────────────────────────────────────────────────────────
// 헬퍼
// ─────────────────────────────────────────────────────────────
function fmtSec(s: number) {
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${String(r).padStart(2, '0')}`
}

function StarRating({ value, onChange }: { value: number; onChange?: (v: number) => void }) {
  const [hover, setHover] = useState(0)
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          onClick={() => onChange?.(n)}
          onMouseEnter={() => onChange && setHover(n)}
          onMouseLeave={() => onChange && setHover(0)}
          className={`transition-colors ${onChange ? 'cursor-pointer' : 'cursor-default'}`}
        >
          <Star
            size={16}
            className={
              n <= (hover || value)
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-slate-300'
            }
          />
        </button>
      ))}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// 영상 카드 (목록용)
// ─────────────────────────────────────────────────────────────
function VideoCard({
  video,
  onSelect,
}: {
  video: ConceptVideo
  onSelect: () => void
}) {
  return (
    <button
      onClick={onSelect}
      className="w-full text-left group border-2 border-slate-200 hover:border-blue-300 rounded-2xl overflow-hidden transition-all hover:shadow-md bg-white"
    >
      {/* 썸네일 */}
      <div className="relative w-full aspect-video bg-slate-100 overflow-hidden">
        <img
          src={video.thumbnailUrl}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={e => {
            (e.target as HTMLImageElement).src =
              'https://placehold.co/320x180/e2e8f0/94a3b8?text=영상'
          }}
        />
        {/* 재생 오버레이 */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
            <Play size={20} className="text-blue-600 ml-1" fill="currentColor" />
          </div>
        </div>
        {/* 길이 배지 */}
        <div className="absolute bottom-2 right-2 bg-black/75 text-white text-xs px-2 py-0.5 rounded font-mono">
          {fmtSec(video.durationSec)}
        </div>
        {/* YouTube 배지 */}
        {video.source === 'youtube' && (
          <div className="absolute top-2 left-2">
            <Youtube size={18} className="text-red-500 drop-shadow" />
          </div>
        )}
      </div>

      {/* 정보 */}
      <div className="p-3">
        <p className="text-sm font-bold text-slate-800 line-clamp-2 mb-1 leading-snug">
          {video.title}
        </p>
        <p className="text-xs text-slate-500 mb-2">{video.creatorName}</p>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="flex items-center gap-1">
            <Eye size={11} /> {video.viewCount.toLocaleString()} 시청
          </span>
          {video.ratingCount > 0 && (
            <span className="flex items-center gap-1">
              <Star size={11} className="text-yellow-400" /> {video.rating.toFixed(1)}
              <span className="text-slate-300">({video.ratingCount})</span>
            </span>
          )}
        </div>
      </div>
    </button>
  )
}

// ─────────────────────────────────────────────────────────────
// 영상 플레이어 뷰
// ─────────────────────────────────────────────────────────────
function VideoPlayer({
  video,
  viewerId,
  onBack,
  onCompleted,
}: {
  video: ConceptVideo
  viewerId: string
  onBack: () => void
  onCompleted: () => void
}) {
  const { startView, completeView, rateVideo } = useVideoStore(s => ({
    startView:   s.startView,
    completeView: s.completeView,
    rateVideo:   s.rateVideo,
  }))

  const [viewId]        = useState(() => startView(video.id, viewerId))
  const [elapsed, setElapsed]   = useState(0)
  const [playing, setPlaying]   = useState(false)
  const [validated, setValidated] = useState(false)
  const [creditEarned, setCreditEarned] = useState(0)
  const [myRating, setMyRating] = useState(0)
  const [rated, setRated]       = useState(false)
  const [done, setDone]         = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // 타이머: iframe 메시지 API 없이 간단히 체크박스로 대체
  // (유튜브 iframe은 실제 재생시간 추적이 복잡하므로
  //  "영상을 봤어요" 버튼 + 타이머로 유효 시청 판정)
  const startTimer = useCallback(() => {
    if (timerRef.current) return
    setPlaying(true)
    timerRef.current = setInterval(() => {
      setElapsed(e => {
        const next = e + 1
        if (next >= MIN_WATCH_SEC && !validated) {
          // 유효 시청 판정
          const result = completeView(viewId, next)
          setValidated(true)
          setCreditEarned(result.creditEarned)
        }
        return next
      })
    }, 1000)
  }, [viewId, validated, completeView])

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    setPlaying(false)
  }, [])

  // 언마운트 시 타이머 정리 & 미완료 시청도 기록
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (!validated) completeView(viewId, elapsed)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRate = (score: number) => {
    setMyRating(score)
    rateVideo(video.id, viewerId, score)
    setRated(true)
  }

  const embedUrl = getYouTubeEmbedUrl(video.youtubeUrl ?? '')

  const pct = Math.min(100, Math.round((elapsed / MIN_WATCH_SEC) * 100))

  return (
    <div className="flex flex-col gap-4">
      {/* 뒤로가기 */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors w-fit"
      >
        <ArrowLeft size={14} /> 영상 목록으로
      </button>

      {/* 영상 제목 */}
      <div>
        <p className="text-xs text-slate-400 mb-0.5">{video.creatorName}</p>
        <h3 className="text-base font-black text-slate-800 leading-snug">{video.title}</h3>
      </div>

      {/* 영상 embed */}
      <div className="rounded-2xl overflow-hidden bg-black aspect-video w-full shadow-lg">
        {embedUrl ? (
          <iframe
            src={embedUrl}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={video.title}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/60 text-sm">
            영상을 불러올 수 없습니다
          </div>
        )}
      </div>

      {/* 시청 진행 트래커 */}
      {!validated ? (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold text-blue-700 flex items-center gap-1.5">
              <Clock size={12} /> 유효 시청 진행 중
            </p>
            <span className="text-xs font-mono text-blue-600">
              {elapsed}s / {MIN_WATCH_SEC}s
            </span>
          </div>
          <div className="bg-blue-100 rounded-full h-2 mb-3">
            <div
              className="h-2 rounded-full bg-blue-500 transition-all duration-1000"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-xs text-blue-600 mb-3">
            영상을 보면서 아래 버튼을 눌러 시청 시간을 기록하세요.
            <br />
            <span className="font-semibold">{MIN_WATCH_SEC}초 이상 시청</span>하면 크리에이터에게{' '}
            <span className="font-bold text-blue-800">{CREDIT_PER_VIEW}원</span>이 지급됩니다.
          </p>
          <div className="flex gap-2">
            {!playing ? (
              <button
                onClick={startTimer}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-500 text-white text-sm font-bold hover:bg-blue-600 transition-colors"
              >
                <Play size={13} fill="white" /> 시청 시작
              </button>
            ) : (
              <button
                onClick={stopTimer}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-300 transition-colors"
              >
                ⏸ 일시 정지
              </button>
            )}
          </div>
        </div>
      ) : (
        /* 유효 시청 완료 */
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={18} className="text-emerald-500" />
            <p className="text-sm font-black text-emerald-700">시청 완료!</p>
            {creditEarned > 0 && (
              <span className="ml-auto text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-bold flex items-center gap-1">
                <Award size={11} /> 크리에이터에게 {creditEarned}원 지급됨
              </span>
            )}
          </div>

          {/* 별점 */}
          {!rated ? (
            <div className="mt-3">
              <p className="text-xs text-slate-500 mb-2">이 영상이 도움이 됐나요? 별점을 남겨주세요!</p>
              <StarRating value={myRating} onChange={handleRate} />
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-2">
              <ThumbsUp size={13} className="text-emerald-500" />
              <p className="text-xs text-emerald-700 font-semibold">평가해 주셔서 감사합니다! ⭐ {myRating}점</p>
            </div>
          )}
        </div>
      )}

      {/* 설명 & 태그 */}
      <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
        <p className="text-xs text-slate-600 leading-relaxed mb-2">{video.description}</p>
        <div className="flex flex-wrap gap-1.5">
          {video.tags.map(t => (
            <span key={t} className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
              #{t}
            </span>
          ))}
        </div>
      </div>

      {/* 복습 완료 버튼 */}
      {validated && (
        <button
          onClick={() => { setDone(true); onCompleted() }}
          className="w-full py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black text-sm flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all"
        >
          <CheckCircle size={16} /> 복습 완료 — 문제로 돌아가기
        </button>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// 메인 모달
// ─────────────────────────────────────────────────────────────
interface ConceptVideoModalProps {
  /** 표시할 개념 태그 (예: '기울기', 'LS05', 'x절편·y절편') */
  conceptTag:   string
  conceptLabel: string
  viewerId?:    string   // 학생 ID (없으면 'guest')
  onClose:      () => void
  onCompleted?: () => void  // 복습 완료 콜백
}

export default function ConceptVideoModal({
  conceptTag,
  conceptLabel,
  viewerId = 'guest',
  onClose,
  onCompleted,
}: ConceptVideoModalProps) {
  const getApprovedByTag   = useVideoStore(s => s.getApprovedByTag)
  const getRecruitingByTag = useVideoStore(s => s.getRecruitingByTag)

  // approved 영상 vs recruiting 슬롯 분리
  const approvedVideos   = getApprovedByTag(conceptTag)
  const recruitingVideos = getRecruitingByTag(conceptTag)
  const hasApproved      = approvedVideos.length > 0
  const hasRecruiting    = recruitingVideos.length > 0 && !hasApproved

  // recruiting 상태에서 파편 슬롯의 step ID 파악
  const recruitingStepId = hasRecruiting
    ? (CONCEPT_TO_STEP[conceptTag] ?? recruitingVideos[0]?.conceptId ?? null)
    : null

  const [selectedVideo, setSelectedVideo] = useState<ConceptVideo | null>(
    approvedVideos.length === 1 ? approvedVideos[0] : null  // approved가 1개면 바로 재생
  )

  // ESC 키로 닫기
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  // 배경 스크롤 잠금
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  const handleCompleted = () => {
    onCompleted?.()
    // 잠시 후 닫기
    setTimeout(onClose, 1800)
  }

  return (
    /* 오버레이 */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      {/* 모달 박스 */}
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto flex flex-col">
        {/* 헤더 */}
        <div className="flex items-start justify-between p-5 border-b border-slate-100 sticky top-0 bg-white z-10 rounded-t-3xl">
          <div className="flex items-center gap-3">
            {/* 파편 학습 아이콘 */}
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center flex-shrink-0">
              <Youtube size={18} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full border border-violet-200">
                  📌 파편 학습 보충
                </span>
              </div>
              <h2 className="text-base font-black text-slate-800 mt-0.5">
                {conceptLabel} — 영상 복습
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors flex-shrink-0 ml-2"
          >
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        {/* 바디 */}
        <div className="p-5 flex-1">
          {selectedVideo ? (
            /* ── ① approved 영상 플레이어 ─────────────────────────── */
            <VideoPlayer
              video={selectedVideo}
              viewerId={viewerId}
              onBack={() => setSelectedVideo(null)}
              onCompleted={handleCompleted}
            />
          ) : hasApproved ? (
            /* ── ② approved 영상 목록 ──────────────────────────────── */
            <div>
              <p className="text-sm text-slate-500 mb-4">
                <span className="font-semibold text-slate-700">{conceptLabel}</span> 개념을
                설명하는 선생님 영상입니다. 골라서 시청하세요!
              </p>
              <div className="grid grid-cols-1 gap-3">
                {approvedVideos.map(v => (
                  <VideoCard
                    key={v.id}
                    video={v}
                    onSelect={() => setSelectedVideo(v)}
                  />
                ))}
              </div>
              <div className="mt-4 border-2 border-dashed border-slate-200 rounded-2xl p-4 text-center">
                <p className="text-xs text-slate-400 mb-1">이 개념을 더 잘 설명할 수 있나요?</p>
                <a href="/video-submit" className="text-xs text-blue-500 hover:text-blue-700 font-semibold underline" onClick={onClose}>
                  + 영상 올리고 크레딧 받기
                </a>
              </div>
            </div>
          ) : hasRecruiting ? (
            /* ── ③ recruiting 슬롯 — 크리에이터 모집 UI ───────────── */
            <div className="space-y-4">
              {/* 모집 중 헤더 */}
              <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-purple-50 border-2 border-violet-200 p-4">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">🎬</span>
                  <div>
                    <p className="text-sm font-black text-violet-800">
                      {conceptLabel} — 파편 영상 모집 중
                    </p>
                    <p className="text-xs text-violet-600 mt-0.5">
                      영상이 올라오면 이 자리에서 바로 시청할 수 있어요
                    </p>
                  </div>
                </div>
                {/* step ID 배지 */}
                {recruitingStepId && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs bg-violet-200 text-violet-800 px-2 py-0.5 rounded-full font-mono font-bold">
                      {recruitingStepId}
                    </span>
                    <span className="text-xs text-violet-500">에 해당하는 파편 학습 영상</span>
                  </div>
                )}
              </div>

              {/* 모집 중인 파편 슬롯 목록 — 어떤 영상이 필요한지 안내 */}
              <div className="space-y-2">
                <p className="text-xs font-bold text-slate-500 flex items-center gap-1.5">
                  <AlertCircle size={11} /> 아래 주제의 영상을 기다리고 있어요
                </p>
                {recruitingVideos.map((v, i) => (
                  <div key={v.id} className="rounded-xl border border-dashed border-violet-200 bg-violet-50/40 p-3">
                    <div className="flex items-start gap-2.5">
                      <span className="w-6 h-6 rounded-full bg-violet-200 text-violet-800 text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        {/* 슬롯 타이틀에서 "[영상 모집 중] " 접두사 제거해 간결하게 표시 */}
                        <p className="text-xs font-bold text-slate-700 leading-snug mb-1">
                          {v.title.replace('[영상 모집 중] ', '')}
                        </p>
                        <p className="text-xs text-slate-500 leading-relaxed mb-1.5">{v.description}</p>
                        <div className="flex flex-wrap gap-1">
                          {v.tags.slice(0, 4).map(t => (
                            <span key={t} className="text-xs bg-white text-slate-500 border border-slate-200 px-1.5 py-0.5 rounded-full">
                              #{t}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 크리에이터 혜택 + CTA */}
              <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-2xl p-4 text-white">
                <p className="text-sm font-black mb-2.5 flex items-center gap-2">
                  <Award size={14} /> 이 영상을 올리면?
                </p>
                <ul className="text-xs space-y-1.5 text-white/90 mb-3">
                  <li>✅ 유효 시청 1회당 <strong className="text-yellow-300">{CREDIT_PER_VIEW}원</strong> 크레딧 자동 적립</li>
                  <li>✅ 영상 5분 이내 · 유튜브 링크면 충분</li>
                  <li>✅ AI 1차 검토 + 관리자 승인 후 즉시 게시</li>
                  <li>✅ 학생 별점 → 우수 영상 상단 고정 노출</li>
                </ul>
                <a
                  href={`/video-submit${recruitingStepId ? `?step=${recruitingStepId}` : ''}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-violet-700 font-black text-sm hover:bg-violet-50 transition-colors shadow"
                  onClick={onClose}
                >
                  <Upload size={12} /> 이 개념 영상 등록 신청하기
                </a>
              </div>

              {/* 텍스트 개념 설명으로 복귀 */}
              <button
                onClick={onClose}
                className="flex items-center gap-1.5 text-sm text-blue-500 hover:text-blue-700 mx-auto"
              >
                <BookOpen size={13} /> 텍스트 개념 설명 보기
              </button>
            </div>
          ) : (
            /* ── ④ 슬롯도 없음 (예외 케이스) ─────────────────────── */
            <div className="text-center py-10">
              <div className="text-5xl mb-3">🎬</div>
              <p className="text-base font-bold text-slate-600 mb-1">아직 등록된 영상이 없어요</p>
              <p className="text-sm text-slate-400 mb-4">
                <span className="font-semibold">{conceptLabel}</span> 개념 영상을 첫 번째로 올려주세요!
              </p>
              <a href="/video-submit" className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl bg-violet-500 text-white font-bold hover:bg-violet-600 transition-colors" onClick={onClose}>
                <Upload size={11} /> 영상 등록 신청하기
              </a>
              <button onClick={onClose} className="mt-3 flex items-center gap-1.5 text-sm text-blue-500 hover:text-blue-700 mx-auto">
                <BookOpen size={13} /> 텍스트 개념 설명 보기
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
